import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_URL } from '../config';

// A signup counts toward hours/capacity only when it's still active.
const isActiveSignup = (s) => s.status !== 'denied' && s.status !== 'cancelled';

// Identity key for grouping signups under a single person across the report.
// Case-insensitive on the name (collapsing whitespace) and digits-only on the
// phone, so "Jane Doe" / "jane doe" / "JANE DOE" with the same number all map
// to one entry instead of three. Phone digits are included so two genuinely
// different people who share a first name don't get collapsed together.
const personKey = (signup) => {
  const name = (signup.name || '').trim().toLowerCase().replace(/\s+/g, ' ');
  const phone = (signup.phone || '').replace(/\D/g, '');
  return `${name}||${phone}`;
};

function AdminHoursView() {
  const [viewMode, setViewMode] = useState('person'); // 'person' or 'department'
  const [loading, setLoading] = useState(false);
  const [personData, setPersonData] = useState([]);
  const [departmentData, setDepartmentData] = useState([]);

  useEffect(() => {
    fetchAllData();
  }, []);

  const fetchAllData = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/timeslots/admin/data`);

      const signupsData = {};
      for (const slot of res.data.timeslots) signupsData[slot.id] = [];
      for (const s of res.data.signups) {
        if (isActiveSignup(s) && signupsData[s.timeslot_id]) {
          signupsData[s.timeslot_id].push(s);
        }
      }

      calculateHours(res.data.timeslots, signupsData, res.data.departments);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const calculateHours = (slots, signupsMap, depts) => {
    // Calculate hours per person
    const personMap = {};
    slots.forEach(slot => {
      const durationMs = new Date(slot.end_time) - new Date(slot.start_time);
      const rawHours = durationMs / (1000 * 60 * 60); // Convert ms to hours
      const durationHours = slot.double_time ? rawHours * 2 : rawHours;
      const deptName = depts.find(d => d.id === slot.department_id)?.name || 'Unknown';

      (signupsMap[slot.id] || []).forEach(signup => {
        const key = personKey(signup);
        if (!personMap[key]) {
          personMap[key] = {
            name: signup.name,
            phone: signup.phone,
            totalHours: 0,
            departments: {}
          };
        }

        personMap[key].totalHours += durationHours;

        if (!personMap[key].departments[deptName]) {
          personMap[key].departments[deptName] = 0;
        }
        personMap[key].departments[deptName] += durationHours;
      });
    });

    // Convert to sorted array
    const personArray = Object.values(personMap)
      .sort((a, b) => b.totalHours - a.totalHours)
      .map(p => ({
        ...p,
        totalHours: parseFloat(p.totalHours.toFixed(2)),
        departments: Object.entries(p.departments).map(([dept, hours]) => ({
          name: dept,
          hours: parseFloat(hours.toFixed(2))
        }))
      }));

    setPersonData(personArray);

    // Calculate hours per department
    const deptMap = {};
    slots.forEach(slot => {
      const durationMs = new Date(slot.end_time) - new Date(slot.start_time);
      const rawHours = durationMs / (1000 * 60 * 60);
      const durationHours = slot.double_time ? rawHours * 2 : rawHours;
      const deptName = depts.find(d => d.id === slot.department_id)?.name || 'Unknown';

      if (!deptMap[deptName]) {
        deptMap[deptName] = {
          name: deptName,
          totalHours: 0,
          volunteers: {}
        };
      }

      deptMap[deptName].totalHours += durationHours * (signupsMap[slot.id] || []).length;

      (signupsMap[slot.id] || []).forEach(signup => {
        const key = personKey(signup);
        if (!deptMap[deptName].volunteers[key]) {
          deptMap[deptName].volunteers[key] = { name: signup.name, hours: 0 };
        }
        deptMap[deptName].volunteers[key].hours += durationHours;
      });
    });

    // Convert to sorted array
    const deptArray = Object.values(deptMap)
      .sort((a, b) => b.totalHours - a.totalHours)
      .map(d => ({
        ...d,
        totalHours: parseFloat(d.totalHours.toFixed(2)),
        volunteers: Object.values(d.volunteers)
          .map(v => ({
            name: v.name,
            hours: parseFloat(v.hours.toFixed(2))
          }))
          .sort((a, b) => b.hours - a.hours)
      }));

    setDepartmentData(deptArray);
  };

  return (
    <div className="hours-view-container">
      <h2>Volunteer Hours Tracker</h2>

      {/* View Toggle */}
      <div className="hours-controls">
        <div className="view-toggle">
          <button
            className={viewMode === 'person' ? 'active' : ''}
            onClick={() => setViewMode('person')}
          >
            By Person
          </button>
          <button
            className={viewMode === 'department' ? 'active' : ''}
            onClick={() => setViewMode('department')}
          >
            By Department
          </button>
        </div>
        <button className="refresh-btn" onClick={fetchAllData} disabled={loading}>
          {loading ? 'Refreshing...' : 'Refresh'}
        </button>
      </div>

      {loading ? (
        <p className="loading-text">Loading volunteer data...</p>
      ) : viewMode === 'person' ? (
        <PersonView data={personData} />
      ) : (
        <DepartmentView data={departmentData} />
      )}
    </div>
  );
}

// Person View Component
function PersonView({ data }) {
  if (data.length === 0) {
    return <div className="no-data">No volunteer data available</div>;
  }

  return (
    <div className="person-view">
      <div className="summary-stats">
        <div className="stat-card">
          <h3>Total Volunteers</h3>
          <p className="stat-number">{data.length}</p>
        </div>
        <div className="stat-card">
          <h3>Total Hours</h3>
          <p className="stat-number">
            {data.reduce((sum, person) => sum + person.totalHours, 0).toFixed(1)}
          </p>
        </div>
        <div className="stat-card">
          <h3>Average Hours</h3>
          <p className="stat-number">
            {(data.reduce((sum, person) => sum + person.totalHours, 0) / data.length).toFixed(1)}
          </p>
        </div>
      </div>

      <div className="person-list">
        {data.map((person, idx) => (
          <div key={person.name} className="person-card">
            <div className="person-header">
              <div className="person-rank">{idx + 1}</div>
              <div className="person-info">
                <h4>{person.name}</h4>
                <span className="person-phone">{person.phone}</span>
              </div>
              <div className="person-total-hours">
                <span className="hours-badge">{person.totalHours} hrs</span>
              </div>
            </div>

            {person.departments.length > 0 && (
              <div className="person-departments">
                <h5>Department Breakdown:</h5>
                <ul className="department-list">
                  {person.departments.map(dept => (
                    <li key={dept.name} className="dept-item">
                      <span className="dept-name">{dept.name}</span>
                      <span className="dept-hours">{dept.hours} hrs</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

// Department View Component
function DepartmentView({ data }) {
  if (data.length === 0) {
    return <div className="no-data">No department data available</div>;
  }

  return (
    <div className="department-view">
      <div className="summary-stats">
        <div className="stat-card">
          <h3>Total Departments</h3>
          <p className="stat-number">{data.length}</p>
        </div>
        <div className="stat-card">
          <h3>Total Hours</h3>
          <p className="stat-number">
            {data.reduce((sum, dept) => sum + dept.totalHours, 0).toFixed(1)}
          </p>
        </div>
      </div>

      <div className="department-list-main">
        {data.map((department, idx) => (
          <div key={department.name} className="department-card">
            <div className="department-header">
              <div className="dept-rank">{idx + 1}</div>
              <div className="dept-info">
                <h4>{department.name}</h4>
              </div>
              <div className="dept-total-hours">
                <span className="hours-badge">{department.totalHours} hrs</span>
              </div>
            </div>

            {department.volunteers.length > 0 && (
              <div className="department-volunteers">
                <h5>Volunteers:</h5>
                <ul className="volunteer-list">
                  {department.volunteers.map(vol => (
                    <li key={vol.name} className="vol-item">
                      <span className="vol-name">{vol.name}</span>
                      <span className="vol-hours">{vol.hours} hrs</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

export default AdminHoursView;
